import { FC, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
	Box,
	Button,
	Container,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useUserStore } from "../../../stores/user.store";
import {
	buildWorkbook,
	triggerDownload,
	ProgramReportConfig,
} from "../../../utils/xlsx-export/build-workbook";
import { useGetPrograms } from "../../../hooks/usePrograms";
import { Program } from "../../../domain/program.interface";
import EnrollmentSummaryTab from "./tabs/EnrollmentSummaryTab";
import IncentiveDisbursementTab from "./tabs/IncentiveDisbursementTab";
import EducationalEngagementTab from "./tabs/EducationalEngagementTab";
import EventActivityTab from "./tabs/EventActivityTab";
import EventRatingsTab from "./tabs/EventRatingsTab";

const SELECTED_PROGRAM_KEY = "reports-selectedProgramId";

interface TabDef {
	id: string;
	label: string;
	alwaysOn?: boolean;
	configKey?:
		| "incentiveDisbursement"
		| "educationalEngagement"
		| "eventActivity"
		| "eventRatings";
}

const TAB_DEFS: TabDef[] = [
	{ id: "enrollment", label: "Enrollment summary", alwaysOn: true },
	{
		id: "incentive",
		label: "Incentive disbursement",
		configKey: "incentiveDisbursement",
	},
	{
		id: "education",
		label: "Educational engagement",
		configKey: "educationalEngagement",
	},
	{ id: "activity", label: "Event activity", configKey: "eventActivity" },
	{ id: "ratings", label: "Event ratings", configKey: "eventRatings" },
];

const EmptyState: FC = () => (
	<Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
		<Typography variant="h6" gutterBottom>
			Select a program to view its reports
		</Typography>
		<Typography variant="body2" color="text.secondary">
			Choose a program from the dropdown above to load its consolidated report
			tabs.
		</Typography>
	</Paper>
);

const ConsolidatedReport: FC = () => {
	const { userInfo, userHasAccessToCode } = useUserStore();

	const accountId = userInfo?.accountId ?? "";
	const { data: programs, isLoading: isProgramsLoading } =
		useGetPrograms(accountId);

	const [selectedProgramId, setSelectedProgramId] = useState<string>(
		() => sessionStorage.getItem(SELECTED_PROGRAM_KEY) ?? "",
	);
	const [activeTabId, setActiveTabId] = useState<string>(TAB_DEFS[0].id);
	const [isExporting, setIsExporting] = useState<boolean>(false);

	// Selection lifted from EventRatingsTab so the Export XLSX button can scope
	// the event-ratings sheets to the same event the user is viewing. Spec
	// requires the export to reflect the SELECTED event (SOWS → 2 sheets,
	// Mood/Sleep → 3 sheets), not every rateable event in the program.
	const [eventRatingsSelection, setEventRatingsSelection] = useState<{
		eventId: string;
		enrollmentId: string;
	}>({ eventId: "", enrollmentId: "" });

	// Reset the per-tab selection whenever the user picks a different program —
	// otherwise an eventId from program A leaks into program B.
	useEffect(() => {
		setEventRatingsSelection({ eventId: "", enrollmentId: "" });
	}, [selectedProgramId]);

	const handleExport = async (): Promise<void> => {
		if (!selectedProgramId || !accountId) return;
		const program = programs?.find((p) => p._id === selectedProgramId);
		if (!program) return;
		setIsExporting(true);
		try {
			const result = await buildWorkbook({
				accountId,
				programId: selectedProgramId,
				programName: program.name,
				reportConfig:
					(program.reportConfig as ProgramReportConfig | undefined) ?? {},
				eventId: eventRatingsSelection.eventId || undefined,
				enrollmentId: eventRatingsSelection.enrollmentId || undefined,
			});
			triggerDownload(result);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("[Reports Export] failed:", err);
		} finally {
			setIsExporting(false);
		}
	};

	useEffect(() => {
		if (selectedProgramId) {
			sessionStorage.setItem(SELECTED_PROGRAM_KEY, selectedProgramId);
		} else {
			sessionStorage.removeItem(SELECTED_PROGRAM_KEY);
		}
	}, [selectedProgramId]);

	// Clear stale programId from sessionStorage when the persisted program
	// no longer exists (e.g., deleted between sessions).
	useEffect(() => {
		if (
			selectedProgramId &&
			!isProgramsLoading &&
			programs &&
			!programs.find((p) => p._id === selectedProgramId)
		) {
			setSelectedProgramId("");
		}
	}, [programs, isProgramsLoading, selectedProgramId]);

	const selectedProgram: Program | undefined = useMemo(
		() => programs?.find((p) => p._id === selectedProgramId),
		[programs, selectedProgramId],
	);

	const visibleTabs = useMemo<TabDef[]>(() => {
		const reportConfig = selectedProgram?.reportConfig;
		return TAB_DEFS.filter(
			(t) => t.alwaysOn || (t.configKey && reportConfig?.[t.configKey]),
		);
	}, [selectedProgram]);

	useEffect(() => {
		if (!visibleTabs.find((t) => t.id === activeTabId)) {
			setActiveTabId(visibleTabs[0]?.id ?? TAB_DEFS[0].id);
		}
	}, [visibleTabs, activeTabId]);

	if (!userHasAccessToCode("REP_CONS_VIEW")) {
		return <Navigate to="/unauthorized" replace />;
	}

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Stack
				direction={{ xs: "column", md: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "stretch", md: "center" }}
				spacing={2}
				sx={{ mb: 3 }}
			>
				<Typography variant="h5">Reports</Typography>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={2}
					alignItems={{ xs: "stretch", sm: "center" }}
				>
					<FormControl size="small" sx={{ minWidth: 260 }}>
						<InputLabel id="reports-program-label">Program</InputLabel>
						<Select
							labelId="reports-program-label"
							label="Program"
							value={selectedProgramId}
							onChange={(e) => setSelectedProgramId(e.target.value)}
							disabled={isProgramsLoading}
						>
							{(programs ?? []).map((p) => (
								<MenuItem key={p._id} value={p._id}>
									{p.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="outlined"
						startIcon={<FileDownloadOutlinedIcon />}
						disabled={!selectedProgramId || isExporting}
						onClick={handleExport}
					>
						{isExporting ? "Exporting…" : "Export XLSX"}
					</Button>
				</Stack>
			</Stack>

			{!selectedProgramId ? (
				<EmptyState />
			) : (
				<>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={activeTabId}
							onChange={(_, v) => setActiveTabId(v as string)}
							variant="scrollable"
							scrollButtons="auto"
						>
							{visibleTabs.map((t) => (
								<Tab key={t.id} label={t.label} value={t.id} />
							))}
						</Tabs>
					</Box>
					<Box sx={{ pt: 3 }}>
						{activeTabId === "enrollment" && (
							<EnrollmentSummaryTab programId={selectedProgramId} />
						)}
						{activeTabId === "incentive" && (
							<IncentiveDisbursementTab programId={selectedProgramId} />
						)}
						{activeTabId === "education" && (
							<EducationalEngagementTab programId={selectedProgramId} />
						)}
						{activeTabId === "activity" && (
							<EventActivityTab programId={selectedProgramId} />
						)}
						{activeTabId === "ratings" && (
							<EventRatingsTab
								programId={selectedProgramId}
								eventId={eventRatingsSelection.eventId}
								enrollmentId={eventRatingsSelection.enrollmentId}
								onSelectionChange={(sel) =>
									setEventRatingsSelection((p) => ({
										eventId: sel.eventId ?? p.eventId,
										enrollmentId: sel.enrollmentId ?? p.enrollmentId,
									}))
								}
							/>
						)}
					</Box>
				</>
			)}
		</Container>
	);
};

export default ConsolidatedReport;
